import type { LimbId } from "../combat/health.js";
import type { WeaponDef } from "../combat/weapons.js";
import type { GridRect, GridState } from "../inventory/grid.js";
import type { ConsumeDef } from "../sim/needs.js";

/**
 * The item vocabulary. Every item is one `ItemDef` (immutable, authored in
 * `db.ts`) plus zero or more `ItemInstance`s (replaced, never mutated); the
 * wire carries instances and def ids only. Sizes are in grid cells.
 *
 * THE NESTING RULE:
 *   1. Contents travel with the instance: a container in a grid carries its
 *      items on `ItemInstance.contents`, so every path that moves an instance
 *      moves the subtree, as it does for `mods`.
 *   2. Equipped capacity is HOISTED: a worn rig/backpack's grid becomes
 *      `Inventory.rig`/`.backpack` and the instance drops `contents`; taking
 *      it off wraps the grid back. One home at a time.
 *   3. Depth is capped at `MAX_GRID_DEPTH` (`inventory/nesting.ts`): a full
 *      backpack in the stash with a full medbox inside it, and nothing further.
 *   4. A host may filter what goes in (`ContainerItemDef.filter`); the filter
 *      binds only the immediate host.
 *
 * Serialization invariant (same as `mods`): `contents` is present only when
 * something is inside, so an emptied box is byte-identical to a fresh one.
 * A magazine's rounds live on the MAGAZINE instance wherever it is
 * (`ItemInstance.mag`). Reload, med targeting and the ammo counters walk
 * top-level grids only — a round in a closed box is stowed, not at hand.
 */

// ---------------------------------------------------------------------------
// Closed unions
// ---------------------------------------------------------------------------

export type CaliberId = "8x57" | "9x19" | "12g" | "7.62x39";

/** Wire/iteration order for calibers. Append-only. */
export const CALIBER_IDS: readonly CaliberId[] = ["8x57", "9x19", "12g", "7.62x39"] as const;

/**
 * Where something bolts onto a gun — or onto a part of one. A host has at
 * most one slot per kind, so a fitted part is addressed by kind alone
 * (`mods["optic"]`); different hosts on one rifle may repeat a kind.
 *
 *   optic       what the gun is aimed through;
 *   muzzle      brakes, chokes, cans;
 *   grip        the support hand's foregrip;
 *   stock       against the shoulder;
 *   mag         GUN-LEVEL ONLY — a part may never declare one (`validateItemDb`);
 *   barrel      the tube; on the Eastern self-loader also the muzzle host;
 *   handguard   the fore-end; a railed one hosts grip and light;
 *   pistolgrip  the firing hand's hold;
 *   receiver    dust/top covers — the optic mount on an AK pattern;
 *   tactical    lights and lasers.
 */
export type AttachKind =
  | "optic"
  | "muzzle"
  | "grip"
  | "stock"
  | "mag"
  | "barrel"
  | "handguard"
  | "pistolgrip"
  | "receiver"
  | "tactical";

/**
 * What a fitted `tactical` part does when on — the renderer needs a verb, not
 * a (private) def id. Mapped in exactly one place, `fittedTacticalKind`; an
 * unknown part stays dark.
 */
export type TacticalKind = "lamp" | "laser";

/**
 * Wire/iteration order for attachment kinds. Append-only: it decides JSON key
 * order.
 */
export const ATTACH_KINDS: readonly AttachKind[] = [
  "optic",
  "muzzle",
  "grip",
  "stock",
  "mag",
  "barrel",
  "handguard",
  "pistolgrip",
  "receiver",
  "tactical",
] as const;

/**
 * How many parts deep a gun may be built: a part on the gun is depth 1, a part
 * on that is 2, no depth 3. A hard bound checked in three places —
 * `validateItemDb` (content cannot express a third level), `attach`
 * ("too-deep") and every recursive walk over `mods`.
 */
export const MAX_MOD_DEPTH = 2;

export type WeaponEquipSlot = "pistol" | "weapon" | "weapon2";

/** Weapon slots in hotkey order: 1 sidearm, 2 primary, 3 secondary primary. */
export const WEAPON_EQUIP_SLOTS: readonly WeaponEquipSlot[] = [
  "pistol",
  "weapon",
  "weapon2",
] as const;

export type EquipSlot = WeaponEquipSlot | "vest" | "helmet" | "rig" | "backpack";

export function isWeaponEquipSlot(slot: EquipSlot): slot is WeaponEquipSlot {
  return (WEAPON_EQUIP_SLOTS as readonly EquipSlot[]).includes(slot);
}

/** Canonical slot order for deterministic enumeration. Append-only. */
export const EQUIP_SLOTS: readonly EquipSlot[] = [
  "pistol",
  "weapon",
  "weapon2",
  "vest",
  "helmet",
  "rig",
  "backpack",
] as const;

export type ItemKind =
  | "weapon"
  | "ammo"
  | "armor"
  | "med"
  | "container"
  | "loot"
  | "attachment"
  | "grenade";

/**
 * Wire/iteration order for item kinds. Append-only. Lets a kind arriving as
 * data be checked at runtime (`validateItemDb`).
 */
export const ITEM_KINDS: readonly ItemKind[] = [
  "weapon",
  "ammo",
  "armor",
  "med",
  "container",
  "loot",
  "attachment",
  "grenade",
] as const;

/** Fire behaviour. Semi is one round per trigger pull; auto holds. */
export type FireMode = "semi" | "auto";

/**
 * What sort of gun this is, in shop terms — a browsing taxonomy the simulation
 * never reads (market category tree). Authored, not derived: calibre, fire
 * mode and slot cannot separate the hunting carbine from the Eastern one.
 * Required so no gun silently lands in a catch-all.
 */
export type WeaponClass =
  | "pistol"
  | "smg"
  | "shotgun"
  | "rifle"
  | "assault"
  | "marksman"
  | "sniper";

/** Wire/iteration order. Append-only, like `ITEM_KINDS` — it is the order the
 *  market's weapon shelves are drawn in. */
export const WEAPON_CLASSES: readonly WeaponClass[] = [
  "pistol",
  "smg",
  "shotgun",
  "rifle",
  "assault",
  "marksman",
  "sniper",
] as const;

// ---------------------------------------------------------------------------
// Defs
// ---------------------------------------------------------------------------

/** Fields every item has, whatever it does. */
export interface ItemDefBase {
  id: string;
  /** Full display name. German alpine flavour; never a real brand. */
  name: string;
  /** Short label for the grid cell, ~8 characters. */
  shortName: string;
  /** Footprint in grid cells at rotation 0. Both >= 1. */
  w: number;
  h: number;
  /** Kilograms per unit — a stack of 30 rounds weighs 30x this. */
  weightKg: number;
  /** Base trader value of ONE unit, in Euro. */
  valueEuro: number;
  /** Units one instance may hold. 1 means the item does not stack. */
  stackMax: number;
  description?: string;
}

/**
 * A firearm. `stats` is the `WeaponDef` the fire path reads verbatim; the
 * item layer adds `caliber`, and `weaponWithAmmo` folds the loaded round's
 * terminals over it.
 */
export interface WeaponItemDef extends ItemDefBase {
  kind: "weapon";
  stats: WeaponDef;
  caliber: CaliberId;
  fireMode: FireMode;
  /** Which shelf this gun is browsed on. See `WeaponClass` — it is a name for
   *  the shop, never an input to the simulation. */
  weaponClass: WeaponClass;
  /**
   * `"weapon"` fits either shoulder slot (`weapon`, `weapon2`); `"pistol"` the
   * holster only. See `canEquipInSlot`.
   */
  equipSlot: "weapon" | "pistol";
  /**
   * Kick as a scale on the carbine (1 = carbine). Client-side feel only, like
   * `StatMods.recoilMult`: the server never reads it; the client's `AimSystem`
   * offsets camera and fired direction together. Required — content may not
   * leave it blank.
   */
  recoil: number;
  /**
   * Points of gun to shoot away (`combat/wear.ts`); `ArmorItemDef.maxDurability`'s
   * twin and the repair bench's denominator. Required. The live value is
   * `ItemInstance.durability`, absent = full, so pre-wear blobs need no migration.
   */
  maxDurability: number;
  /**
   * What bolts onto this gun, in application order (multipliers compound in
   * this order). Absent = takes nothing. At most one slot per `AttachKind`.
   * The walk is DEPTH-FIRST — see `fittedModsDeep`; float multiplication is
   * not associative, so the traversal is the contract.
   */
  slots?: readonly WeaponSlotDef[];
  /**
   * How R feeds this gun. Absent = `"swap"` (a spare magazine trades places
   * with the fitted one). `"topup"` pushes loose shells into the fitted
   * magazine one at a time — the shotgun only. Separate plans in `planReload`.
   */
  reloadStyle?: "topup";
}

/**
 * One mounting point with an explicit whitelist. `accepts` names def ids
 * rather than a compatibility rule so both sides answer "does this fit?"
 * identically; `validateItemDb` proves every id is an attachment of the kind.
 */
export interface WeaponSlotDef {
  /** The kind this slot holds. Doubles as the key in `ItemInstance.mods`. */
  id: AttachKind;
  /** Def ids that may be fitted here. Never empty. */
  accepts: readonly string[];
  /**
   * Caption for a feature that is part of the host and cannot come off (the
   * shotgun's bead, pump, thread cap), shown where an empty slot would read as
   * a hole. Nothing mints or folds it; detachable factory parts are `factoryDefId`.
   */
  defaultName?: string;
  /**
   * The gun does not work with this slot empty — on the gun or any fitted part,
   * recursively (`weaponComplete`; `acceptFire` refuses "incomplete"). `true`
   * or absent, never `false`. Every gun's barrel is vital; the schema v8
   * migration minted factory parts onto stored guns so no profile loads a
   * rifle the fire path refuses.
   */
  vital?: true;
  /**
   * The part this host ships with, minted (recursively) by `mintFactoryMods`.
   * Must be in `accepts`. Never on a `mag` slot — the first true magazine in
   * `accepts` is the factory box (`factoryMagFor`). Absent = ships empty.
   */
  factoryDefId?: string;
}

/**
 * What fitting an attachment changes. A closed set so content cannot invent a
 * stat nothing reads; absent = untouched (never 1 or 0). Multipliers compound;
 * `ergoAdd` sums; `zeroRangeSetM` replaces (last slot wins). See
 * `aggregateWeaponStats`. Magazine capacity is not here — it is the
 * magazine's own `magCapacity`.
 */
export interface StatMods {
  /**
   * Added to `ergonomics` (0–100, may be negative, clamped after). The ONLY
   * way an attachment moves `adsTimeSec`, which is recomputed via
   * `adsTimeForErgo` — so +8 ergo is worth the same on every gun.
   */
  ergoAdd?: number;
  /** Scales `hipSpreadDeg`. Below 1 is a tighter hip cone. */
  hipSpreadMult?: number;
  /**
   * Scales the client's recoil kick. Client-side feel only; never enters
   * `WeaponStats`. `recoilMultiplier` is the accessor.
   */
  recoilMult?: number;
  /** Scales `muzzleVelocity` — flight time and drop, not damage. */
  muzzleVelocityMult?: number;
  /** Scales `reloadSec`. Above 1 is the price of the bigger magazine. */
  reloadSecMult?: number;
  /** REPLACES `zeroRangeM` outright. A scope is zeroed where its turret says. */
  zeroRangeSetM?: number;
  /**
   * ADS fov divisor (4 = 4×). Client-only; `opticZoom` is the accessor. Absent
   * on an optic means 1×, not "inherit" — the factory glass is an ordinary
   * part with its own `zoom`.
   */
  zoom?: number;
  /**
   * Muzzle report suppressed. Client-only audio; AI hearing keys off the `shot`
   * event, not this. `hasSuppressor` is the accessor.
   */
  suppressed?: boolean;
}

/**
 * A thing that bolts onto a gun. Stateless (def + iid, effect = `modifiers`
 * folded by `aggregateWeaponStats`; never stacks) — except a TRUE MAGAZINE
 * (`magCapacity`), whose rounds are state on its own instance. A part may
 * host parts (`slots`) and is still stateless.
 */
export interface AttachmentItemDef extends ItemDefBase {
  kind: "attachment";
  attachKind: AttachKind;
  modifiers: StatMods;
  /**
   * What bolts onto THIS part, in application order — a weapon's `slots` one
   * level down, one slot per kind. Absent = leaf. Two extra rules
   * (`validateItemDb`): no `mag` slot (rounds are gun-level), and a part at
   * `MAX_MOD_DEPTH` declares none. Children fold in depth-first right after
   * the part (`fittedModsDeep`).
   */
  slots?: readonly WeaponSlotDef[];
  /**
   * Rounds this magazine holds; its presence marks a true magazine. Only with
   * `attachKind: "mag"`, integer ≥ 1. A gun's `magSize` is whatever is
   * fitted, zero when nothing is. `isTrueMagazine` is the accessor.
   */
  magCapacity?: number;
  /**
   * The cartridge this magazine feeds; present exactly when `magCapacity` is.
   * Lets a loose magazine refuse the wrong ammo without a gun.
   */
  caliber?: CaliberId;
}

/**
 * An attachment that is a true magazine, both optional fields narrowed. What
 * `isTrueMagazine` proves.
 */
export type MagazineItemDef = AttachmentItemDef & {
  magCapacity: number;
  caliber: CaliberId;
};

/**
 * A cartridge. `damage`/`penPower` replace the weapon's when loaded;
 * `velocityMult` scales muzzle velocity; `pellets` (1, or 8 for buckshot) each
 * fly as their own projectile with full `damage`.
 */
export interface AmmoItemDef extends ItemDefBase {
  kind: "ammo";
  caliber: CaliberId;
  damage: number;
  penPower: number;
  /** Multiplies the weapon's `muzzleVelocity`. 1 = the weapon's own number. */
  velocityMult: number;
  /** Projectiles per trigger pull. 1 for bullets, 8 for buckshot. */
  pellets: number;
}

/**
 * A plate carrier or helmet. The numbers are `ArmorState`'s; the instance's
 * `durability` is the live value.
 */
export interface ArmorItemDef extends ItemDefBase {
  kind: "armor";
  equipSlot: "vest" | "helmet";
  armorClass: number;
  maxDurability: number;
  /** Limbs this piece covers. Vests are torso, helmets are head. */
  zones: readonly LimbId[];
}

/** What using one charge of a med actually does. */
export type MedEffect =
  | { t: "stopBleed" }
  | { t: "fixFracture" }
  | { t: "heal"; hpPerCharge: number; alsoStopsBleed: boolean };

/**
 * A medical item. `charges` is uses when fresh; the live count rides on the
 * instance's `durability`. Targeting: `resolveMedTarget`.
 */
export interface MedItemDef extends ItemDefBase {
  kind: "med";
  /** Seconds the use takes. The server runs the clock; the client shows it. */
  useTimeSec: number;
  charges: number;
  effect: MedEffect;
}

/** What a grenade does when the fuse runs out. */
export type GrenadeEffect =
  /** Blast: `damage` at the centre, linear falloff to 0 at `radiusM`, blocked by walls. */
  | { t: "frag"; radiusM: number; damage: number }
  /** A cloud that blocks scav line of sight for `durationSec`. */
  | { t: "smoke"; radiusM: number; durationSec: number }
  /** Blinds anyone within `radiusM` with line of sight for `durationSec`. */
  | { t: "flash"; radiusM: number; durationSec: number };

/**
 * A hand grenade. `fuseSec` runs from ARMING, not the throw. Lives in any grid;
 * no equip slot.
 */
export interface GrenadeItemDef extends ItemDefBase {
  kind: "grenade";
  fuseSec: number;
  effect: GrenadeEffect;
}

/**
 * A rig, backpack or storage box. `gridW`/`gridH` is the capacity; a box
 * (`equipSlot: "none"`) must exceed its own footprint (`validateItemDb`), a
 * worn container pays no cells so may equal it. Where the capacity lives: see
 * nesting rule clauses 1–2.
 */
export interface ContainerItemDef extends ItemDefBase {
  kind: "container";
  /**
   * Slot this is worn in, or `"none"` for a storage box (`equipSlotOf` →
   * `null`, `equip` → "wrong-slot").
   */
  equipSlot: "rig" | "backpack" | "none";
  gridW: number;
  gridH: number;
  /**
   * Pouches, as rectangles in the container's cell space. Absent = one open
   * compartment. An item must fit wholly inside one pouch (`placementProblem`
   * answers "bounds"); cells outside every pouch are dead, so capacity is the
   * sum of the pouches (`containerCapacity`). `validateItemDb` proves them
   * integral, non-empty, inside the field and non-overlapping.
   */
  layout?: readonly GridRect[];
  /**
   * Item kinds this grid accepts; absent = anything. Binds the immediate host
   * only. `validateItemDb`: real kinds, never empty, never `"container"` (a
   * filtered box is always a leaf).
   */
  filter?: readonly ItemKind[];
}

/** Barter goods and cash: worth Euro, worth nothing else. */
export interface LootItemDef extends ItemDefBase {
  kind: "loot";
  /**
   * A quest deliverable: outside the economy entirely, handed over via turn-in
   * only. `true` or absent, never `false`.
   */
  questItem?: true;
  /**
   * Edible/drinkable: what one unit puts back on the needs meters and how
   * long it takes. Still `kind: "loot"` (bacon is barter and ingredient before
   * it is food); the verb hangs off this block. Never on a quest item.
   */
  consume?: ConsumeDef;
}

export type ItemDef =
  | WeaponItemDef
  | AmmoItemDef
  | ArmorItemDef
  | MedItemDef
  | ContainerItemDef
  | LootItemDef
  | AttachmentItemDef
  | GrenadeItemDef;

// ---------------------------------------------------------------------------
// Instances
// ---------------------------------------------------------------------------

/**
 * One concrete thing in the world. `iid` is raid-unique and server-minted.
 * Treat as IMMUTABLE: every function returns replacements, so grids can share
 * instance objects between old and new state.
 */
export interface ItemInstance {
  iid: string;
  defId: string;
  /** Units in this stack. Always >= 1 and <= the def's `stackMax`. */
  stack: number;
  /**
   * Armour durability, med charges, or weapon wear — decided by the def's
   * `kind`. On a weapon absent means full (`combat/wear.ts`), so pre-wear blobs
   * need no migration. `weaponDurability` is the accessor.
   */
  durability?: number;
  /**
   * Reduced ceiling of a repaired plate or gun: each repair shaves
   * `REPAIR_MAX_LOSS_FRAC` of the def's maximum (`economy/repair.ts`). Present
   * only once below the def's number; absent = the def's number.
   * `armorMaxDurability`/`weaponMaxDurability` are the accessors.
   */
  maxDurability?: number;
  /**
   * Rounds loaded. True magazines only, fitted or loose — the rounds belong to
   * the box wherever it is. A weapon never carries this key; ask
   * `loadedRounds`/`loadedAmmo`. Absent on an empty magazine.
   */
  mag?: number;
  /**
   * Which cartridge those rounds are. Absent whenever `mag` is 0, so an emptied
   * magazine is byte-identical to a fresh one.
   */
  ammoDefId?: string;
  /**
   * The player has counted this magazine, so the HUD prints a number instead
   * of "?". Full and empty boxes are always known (`magCountKnown` folds that
   * in). Set by `magCheck`, deleted by every round fired, left alone by
   * reloads and loads; every magazine in the kit is marked on join. Only ever
   * `true`, deleted rather than set false. A display concept — the true `mag`
   * still travels beside it.
   */
  magKnown?: true;
  /**
   * Fitted attachments by `AttachKind`, present only when at least one is
   * fitted. Values are stack-1 instances (a true magazine keeps its rounds on
   * its own instance here). They travel with the gun through every path that
   * moves an instance. A fitted part may itself carry `mods`, same invariant
   * at every level; nesting is capped at `MAX_MOD_DEPTH`, separate from
   * `MAX_GRID_DEPTH` (`mods` is not a grid and never holds a container).
   */
  mods?: { [slotId: string]: ItemInstance };
  /**
   * What is inside this container, as a full grid. Present only when at least
   * one item is inside (`applyInventoryOp` normalizes the key away). Only
   * `kind: "container"` defs, always the def's `gridW`/`gridH`. Never on an
   * EQUIPPED rig/backpack — hoisted onto `Inventory.rig`/`.backpack`.
   */
  contents?: GridState;
}

// ---------------------------------------------------------------------------
// Small helpers over the union
// ---------------------------------------------------------------------------

/**
 * The slot this def is worn in, or `null`. A storage box (`equipSlot: "none"`)
 * answers `null`, which keeps it out of every equip path.
 */
export function equipSlotOf(def: ItemDef): EquipSlot | null {
  switch (def.kind) {
    case "weapon":
    case "armor":
      return def.equipSlot;
    case "container":
      return def.equipSlot === "none" ? null : def.equipSlot;
    default:
      return null;
  }
}

/**
 * Long guns fit either shoulder slot, never the holster; a pistol the holster
 * only. Other gear keeps its one slot.
 */
export function canEquipInSlot(def: ItemDef, slot: EquipSlot): boolean {
  if (def.kind !== "weapon") return equipSlotOf(def) === slot;
  return def.equipSlot === "pistol" ? slot === "pistol" : slot === "weapon" || slot === "weapon2";
}

/** True when more than one unit fits in a single instance. */
export function isStackable(def: ItemDef): boolean {
  return def.stackMax > 1;
}

/**
 * True for a quest deliverable (`LootItemDef.questItem`) — the one exemption
 * three validators and the sell counter share.
 */
export function isQuestItem(def: ItemDef | null): boolean {
  return def !== null && def.kind === "loot" && def.questItem === true;
}
